import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';

const Input = forwardRef(({
  label,
  type = 'text',
  error,
  helperText,
  fullWidth = true,
  required = false,
  leftIcon,
  rightIcon,
  className = '',
  ...props
}, ref) => {
  const baseClasses = 'px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 text-sm';
  
  const errorClasses = error 
    ? 'border-red-500 focus:ring-red-500' 
    : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500';
  
  const widthClass = fullWidth ? 'w-full' : '';
  
  // Handle icon rendering - allow both React elements and string class names
  const renderIcon = (icon, position) => {
    if (!icon) return null;
    
    if (React.isValidElement(icon)) {
      return React.cloneElement(icon, {
        className: `h-5 w-5 text-gray-400 ${icon.props.className || ''}`
      });
    }
    
    return <div className={`h-5 w-5 text-gray-400 ${icon}`} />;
  };

  const classes = `
    ${baseClasses}
    ${errorClasses}
    ${widthClass}
    ${leftIcon ? 'pl-10' : ''}
    ${rightIcon ? 'pr-10' : ''}
    ${className}
  `.trim();

  return (
    <div className={`${fullWidth ? 'w-full' : ''}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <div className="relative">
        {leftIcon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            {renderIcon(leftIcon)}
          </div>
        )}
        
        <input
          ref={ref}
          type={type}
          className={classes}
          aria-invalid={error ? 'true' : 'false'}
          {...props}
        />
        
        {rightIcon && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            {renderIcon(rightIcon)}
          </div>
        )}
      </div>
      
      {(error || helperText) && (
        <p className={`mt-1 text-sm ${error ? 'text-red-600' : 'text-gray-500'}`}>
          {error || helperText}
        </p>
      )}
    </div>
  );
});

Input.propTypes = {
  label: PropTypes.string,
  type: PropTypes.string,
  error: PropTypes.string,
  helperText: PropTypes.string,
  fullWidth: PropTypes.bool,
  required: PropTypes.bool,
  leftIcon: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  rightIcon: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  className: PropTypes.string
};

Input.displayName = 'Input';

export default Input;